/**
 * Price monitoring (Prisövervakning) preferences.
 *
 * The date range and channel selection follow the user across every brand;
 * the product selection is remembered per brand. Every field is optional and
 * a missing field means "use the default".
 */

import { DATE_PRESET_KEYS, type DatePreset } from '../shared/date-preset';

const PRESETS = DATE_PRESET_KEYS;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

export interface PriceMonitorPrefs {
  /** Preset id; the range is recomputed from today on load. */
  preset?: DatePreset;
  /** Custom range, kept only while no preset matches it. */
  dateStart?: string;
  dateEnd?: string;
  /** Selected channel keys; applied to whichever of them the shown products offer. */
  vendors?: string[];
  /** Selected product EANs per brand slug. */
  productsByBrand?: Record<string, string[]>;
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return !!raw && typeof raw === 'object' && !Array.isArray(raw);
}

function stringList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function ymd(raw: unknown): string | undefined {
  return typeof raw === 'string' && YMD.test(raw) ? raw : undefined;
}

export function mergePriceMonitor(raw: unknown): PriceMonitorPrefs {
  if (!isRecord(raw)) return {};
  const out: PriceMonitorPrefs = {};
  const preset = raw['preset'];
  if (typeof preset === 'string' && (PRESETS as readonly string[]).includes(preset)) {
    out.preset = preset as DatePreset;
  }
  const dateStart = ymd(raw['dateStart']);
  const dateEnd = ymd(raw['dateEnd']);
  if (dateStart && dateEnd && dateStart <= dateEnd) {
    out.dateStart = dateStart;
    out.dateEnd = dateEnd;
  }
  const vendors = stringList(raw['vendors']);
  if (vendors) out.vendors = vendors;
  if (isRecord(raw['productsByBrand'])) {
    const byBrand: Record<string, string[]> = {};
    for (const [brand, eans] of Object.entries(raw['productsByBrand'])) {
      const list = stringList(eans);
      if (brand && list && list.length > 0) byBrand[brand] = list;
    }
    if (Object.keys(byBrand).length > 0) out.productsByBrand = byBrand;
  }
  return out;
}
